import { ReactNode, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SignOut, Translate, UserCircle } from '@phosphor-icons/react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui-kit/dropdown-menu';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { PresenceAvatar } from '@/components/core/presence-avatar/presence-avatar';
import { getLanguageDisplayName } from '@/components/core/language-selector/utils/language-display-name';
import { APP_PATHS } from '@/constant/app-paths';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { useHasPermission } from '@/hooks/use-has-permission';
import { useCurrentOrganizationName } from '@/hooks/use-organizations';
import { useLanguageContext } from '@/i18n/language-context';
import { useSignoutMutation } from '@/modules/auth/hooks/use-auth';
import { useGetAccount } from '@/modules/profile/hooks/use-account';
import { useEmployeesByUserIds } from '@/modules/shared/hr-lookups';
// The file itself, not the module index: the index pulls in the attendance pages, which import core.
import { useAttendancePresence } from '@/modules/time-attendance/attendance/components/attendance-status-link/attendance-status-link';
import { useAuthStore } from '@/state/store/auth';
import { OrganizationMenuSection } from './organization-menu-section';

const getInitialsFromName = (name: string): string => {
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return '?';
};

/** The avatar with today's presence ring and dot; only rendered for who may open Attendance. */
const AvatarWithPresence = ({ children }: { children: ReactNode }) => {
  const { presence } = useAttendancePresence();
  return <PresenceAvatar presence={presence}>{children}</PresenceAvatar>;
};

/**
 * The top bar's profile bubble and account menu (HR Module Prototype, Component Map › Profile bubble +
 * account menu): the avatar with a presence ring and status dot, and a menu headed by the person's name,
 * designation and country (the organization). It offers My Profile, the organization and the
 * language, and Log out (the theme is switched in the top bar, on phones too).
 */
export const ProfileMenu = () => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const { t } = useTranslation();
  const { currentLanguage, setLanguage, availableLanguages } = useLanguageContext();

  const { logout } = useAuthStore();
  const userId = useAuthStore((state) => state.user?.itemId ?? '');
  const { mutateAsync } = useSignoutMutation();
  const navigate = useNavigate();
  const { data, isLoading } = useGetAccount();
  const { byUserId } = useEmployeesByUserIds(userId ? [userId] : []);
  const designation = byUserId.get(userId)?.Designation;
  const { data: organizationName } = useCurrentOrganizationName();
  const canOpenAttendance = useHasPermission(HRM_PERMISSIONS.TIME_ATTENDANCE.VIEW_ATTENDANCE);

  const signoutHandler = async () => {
    try {
      const res = await mutateAsync();
      if (res.isSuccess) {
        logout();
        navigate('/login');
      }
    } catch (_error) {
      /* empty */
    }
  };

  const fullName = `${data?.firstName ?? ''} ${data?.lastName ?? ''}`.trim();
  const initials = fullName
    ? getInitialsFromName(fullName)
    : (data?.email?.slice(0, 2).toUpperCase() ?? '?');
  const profileImageUrl = data?.profileImageUrl?.trim() ?? '';
  const showImage = Boolean(profileImageUrl) && !imageError;

  useEffect(() => {
    setIsImageLoaded(false);
    setImageError(false);
  }, [profileImageUrl]);

  useEffect(() => {
    const img = imgRef.current;
    if (img && img.complete && img.naturalHeight !== 0) setIsImageLoaded(true);
  }, [profileImageUrl, isLoading]);

  const face = showImage ? (
    <>
      {!isImageLoaded && <Skeleton className="absolute inset-0 rounded-full" />}
      <img
        ref={imgRef}
        src={profileImageUrl}
        alt=""
        className="size-full object-cover"
        style={{ opacity: isImageLoaded ? 1 : 0 }}
        onLoad={() => setIsImageLoaded(true)}
        onError={() => setImageError(true)}
      />
    </>
  ) : (
    <span>{initials}</span>
  );

  const go = (path: string) => {
    setIsDropdownOpen(false);
    navigate(path);
  };

  return (
    <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="inline-flex h-9 shrink-0 items-center rounded-md px-1 outline-none hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={fullName ? `${t('YOUR_ACCOUNT')}: ${fullName}` : t('YOUR_ACCOUNT')}
        >
          {isLoading ? (
            <Skeleton className="size-8 rounded-full" />
          ) : canOpenAttendance ? (
            <AvatarWithPresence>{face}</AvatarWithPresence>
          ) : (
            <PresenceAvatar>{face}</PresenceAvatar>
          )}
        </button>
      </DropdownMenuTrigger>
      {/* A 240px menu on phones too, not the full-width phone menu (user, 3 Nov 2026). */}
      <DropdownMenuContent
        className="w-60 max-sm:w-60 max-sm:max-w-[calc(100vw-16px)]"
        align="end"
        sideOffset={8}
      >
        <DropdownMenuLabel className="flex flex-col gap-0.5 font-normal">
          <b className="truncate text-sm font-semibold text-foreground">
            {fullName || data?.email}
          </b>
          {designation && (
            <span className="truncate text-xs text-muted-foreground">{designation}</span>
          )}
          {organizationName && (
            <span className="truncate text-xs text-muted-foreground">{organizationName}</span>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => go(APP_PATHS.MY_PROFILE)}>
          <UserCircle className="size-4" />
          {t('MY_PROFILE')}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <OrganizationMenuSection onOrganizationSelected={() => setIsDropdownOpen(false)} />
        {availableLanguages?.length > 1 && (
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <Translate className="size-4" />
              {t('LANGUAGE')}
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              <DropdownMenuRadioGroup value={currentLanguage} onValueChange={setLanguage}>
                {availableLanguages.map((language) => (
                  <DropdownMenuRadioItem key={language.itemId} value={language.languageCode}>
                    {getLanguageDisplayName(language.languageName, language.languageCode)}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={signoutHandler}>
          <SignOut className="size-4" />
          {t('LOG_OUT')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
